import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Calendar,
  Users,
  FileText,
  ClipboardCheck,
  TrendingUp,
  Plus,
  Upload,
  Image,
  Trash2,
  Edit,
  Clock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  Download,
  Eye,
  Shield,
  Camera
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge component
const StatusBadge = ({ status, size = "default" }) => {
  const statusConfig = {
    active: { color: "bg-green-500/20 text-green-400 border-green-500/30", label: "Active" },
    completed: { color: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Completed" },
    on_hold: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "On Hold" },
    cancelled: { color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Cancelled" },
    ok: { color: "bg-green-500/20 text-green-400 border-green-500/30", label: "OK" },
    needs_repair: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Needs Repair" },
    unsafe: { color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Unsafe" },
    approved: { color: "bg-green-500/20 text-green-400 border-green-500/30", label: "Approved" },
    conditional: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Conditional" },
    rejected: { color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Rejected" }
  };
  
  const config = statusConfig[status] || { color: "bg-gray-500/20 text-gray-400", label: status };
  
  return (
    <Badge className={`${config.color} border ${size === "sm" ? "text-xs" : ""}`}>
      {config.label}
    </Badge>
  );
};

// SJA List Component
const SJAList = ({ projectId }) => {
  const navigate = useNavigate();
  const [sjas, setSjas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSJAs();
  }, [projectId]);

  const fetchSJAs = async () => {
    try {
      const res = await axios.get(`${API}/local-projects/${projectId}/sja`);
      setSjas(res.data || []);
    } catch (error) {
      console.error("Failed to fetch SJAs:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case "green": return "bg-green-500/20 text-green-400 border-green-500/50";
      case "yellow": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/50";
      case "red": return "bg-red-500/20 text-red-400 border-red-500/50";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  const getHighestRisk = (hazards) => {
    if (!hazards || hazards.length === 0) return null;
    if (hazards.some(h => h.risk_level === "red")) return "red";
    if (hazards.some(h => h.risk_level === "yellow")) return "yellow";
    return "green";
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-[#23F7DD]" />
      </div>
    );
  }

  if (sjas.length === 0) {
    return (
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardContent className="py-12 text-center">
          <Shield className="w-12 h-12 text-red-400/50 mx-auto mb-4" />
          <p className="text-[#94A3B8]">No SJA forms created yet</p>
          <p className="text-sm text-[#64748B] mt-1">Click "New SJA" to create a safety analysis</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {sjas.map((sja) => {
        const highestRisk = getHighestRisk(sja.hazards);
        return (
          <Card
            key={sja.sja_id}
            className="bg-[#0A0A0A] border-white/10 hover:border-red-500/30 transition-colors cursor-pointer"
            onClick={() => navigate(`/local-projects/${projectId}/sja/${sja.sja_id}`)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">{sja.sja_code}</h4>
                    <p className="text-xs text-[#64748B]">{sja.work_date || sja.prepared_date}</p>
                  </div>
                </div>
                {highestRisk && (
                  <Badge variant="outline" className={getRiskColor(highestRisk)}>
                    {highestRisk === "red" ? "High Risk" : highestRisk === "yellow" ? "Medium Risk" : "Low Risk"}
                  </Badge>
                )}
              </div>
              
              {sja.job_description && (
                <p className="text-sm text-[#94A3B8] line-clamp-2 mb-3">{sja.job_description}</p>
              )}
              
              <div className="flex items-center justify-between text-xs text-[#64748B]">
                <span className="flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {sja.hazards?.length || 0} hazards
                </span>
                <span>By: {sja.prepared_by}</span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Progress Entry Component
const ProgressEntry = ({ entry }) => {
  return (
    <Card className="bg-white/5 border-white/10">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-medium text-white">{entry.title}</h4>
            <p className="text-xs text-[#64748B]">
              {entry.reported_by_name} • {new Date(entry.created_at).toLocaleString()}
            </p>
          </div>
          {entry.progress_percentage !== null && (
            <Badge className="bg-[#23F7DD]/20 text-[#23F7DD]">
              {entry.progress_percentage}%
            </Badge>
          )}
        </div>
        
        {entry.description && (
          <p className="text-sm text-[#94A3B8] mb-3">{entry.description}</p>
        )}
        
        {entry.work_done_today && (
          <div className="text-sm mb-2">
            <span className="text-[#64748B]">Work done: </span>
            <span className="text-white">{entry.work_done_today}</span>
          </div>
        )}
        
        {entry.issues_encountered && (
          <div className="text-sm mb-2 p-2 bg-red-500/10 rounded">
            <span className="text-red-400">Issues: </span>
            <span className="text-[#94A3B8]">{entry.issues_encountered}</span>
          </div>
        )}
        
        {entry.photos && entry.photos.length > 0 && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {entry.photos.map((photo, idx) => (
              <a key={idx} href={photo} target="_blank" rel="noopener noreferrer">
                <img 
                  src={photo} 
                  alt={`Progress ${idx + 1}`}
                  className="w-20 h-20 object-cover rounded border border-white/10 hover:border-[#23F7DD]/50 transition-colors"
                />
              </a>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Control Report Card
const ControlReportCard = ({ report, onClick }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case "approved": return <CheckCircle2 className="w-5 h-5 text-green-400" />;
      case "conditional": return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case "rejected": return <XCircle className="w-5 h-5 text-red-400" />;
      default: return <ClipboardCheck className="w-5 h-5 text-[#94A3B8]" />;
    }
  };

  return (
    <Card 
      className="bg-white/5 border-white/10 hover:border-[#23F7DD]/30 transition-colors cursor-pointer"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon(report.overall_status)}
            <div>
              <p className="font-mono text-[#23F7DD] text-sm">{report.report_code}</p>
              <p className="text-xs text-[#64748B]">{report.inspection_date}</p>
            </div>
          </div>
          <StatusBadge status={report.overall_status} size="sm" />
        </div>
        
        <div className="mt-3 flex items-center gap-4 text-xs text-[#64748B]">
          <span>{report.scaffolds?.length || 0} scaffolds</span>
          <span>•</span>
          <span>Inspector: {report.inspector_name}</span>
        </div>
      </CardContent>
    </Card>
  );
};

// Add Progress Dialog
const AddProgressDialog = ({ open, onOpenChange, projectId, onAdded }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    progress_percentage: "",
    work_done_today: "",
    issues_encountered: "",
    next_steps: "",
    photos: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error("Please enter a title");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        progress_percentage: formData.progress_percentage ? parseInt(formData.progress_percentage) : null
      };
      
      await axios.post(`${API}/local-projects/${projectId}/progress`, payload);
      toast.success("Progress added!");
      onOpenChange(false);
      onAdded();
      setFormData({
        title: "",
        description: "",
        progress_percentage: "",
        work_done_today: "",
        issues_encountered: "",
        next_steps: "",
        photos: []
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add progress");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#23F7DD]" />
            Add Daily Progress
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Day 3 - Assembly Progress"
              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Progress Percentage</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.progress_percentage}
              onChange={(e) => setFormData({ ...formData, progress_percentage: e.target.value })}
              placeholder="e.g., 45"
              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Work Done Today</Label>
            <Textarea
              value={formData.work_done_today}
              onChange={(e) => setFormData({ ...formData, work_done_today: e.target.value })}
              placeholder="Describe the work completed..."
              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Issues Encountered</Label>
            <Textarea
              value={formData.issues_encountered}
              onChange={(e) => setFormData({ ...formData, issues_encountered: e.target.value })}
              placeholder="Any problems or delays..."
              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Progress
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function LocalProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("progress");
  const [addProgressOpen, setAddProgressOpen] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/local-projects/${projectId}`);
      setProject(res.data);
    } catch (error) {
      console.error("Error fetching project:", error);
      if (error.response?.status === 403) {
        toast.error("You don't have access to this project");
        navigate("/local-projects");
      } else {
        toast.error("Failed to load project");
      }
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = user?.role === "platform_admin";

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Loading...">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-10 h-10 text-[#23F7DD] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Project Not Found">
        <div className="text-center py-16">
          <p className="text-[#94A3B8]">Project not found or you don't have access.</p>
          <Button onClick={() => navigate("/local-projects")} className="mt-4">
            Back to Projects
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={adminNavItems} title={project.title}>
      <div className="space-y-6" data-testid="local-project-detail">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/local-projects")}
              className="text-[#94A3B8] hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Unbounded']">{project.title}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1 text-sm text-[#94A3B8]">
                  <MapPin className="w-4 h-4" />
                  {project.location_address}, {project.location_city}
                </span>
                <StatusBadge status={project.status} />
              </div>
            </div>
          </div>
          
          {isAdmin && (
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Project
            </Button>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 text-[#23F7DD] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{project.progress_entries?.length || 0}</p>
              <p className="text-xs text-[#64748B]">Progress Updates</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 text-center">
              <ClipboardCheck className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{project.control_reports?.length || 0}</p>
              <p className="text-xs text-[#64748B]">Control Reports</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 text-center">
              <FileText className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{project.documents?.length || 0}</p>
              <p className="text-xs text-[#64748B]">Documents</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{project.required_crew_size || 0}</p>
              <p className="text-xs text-[#64748B]">Crew Size</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#0A0A0A] border border-white/10 p-1">
            <TabsTrigger value="progress" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              <TrendingUp className="w-4 h-4 mr-2" />
              Progress
            </TabsTrigger>
            <TabsTrigger value="sja" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              <Shield className="w-4 h-4 mr-2" />
              SJA
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              <FileText className="w-4 h-4 mr-2" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="control-reports" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Control Reports
            </TabsTrigger>
          </TabsList>

          {/* Progress Tab */}
          <TabsContent value="progress" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-white">Daily Progress</h3>
              <Button
                onClick={() => setAddProgressOpen(true)}
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Progress
              </Button>
            </div>
            
            {project.progress_entries && project.progress_entries.length > 0 ? (
              <div className="space-y-4">
                {project.progress_entries.map((entry) => (
                  <ProgressEntry key={entry.progress_id} entry={entry} />
                ))}
              </div>
            ) : (
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardContent className="py-12 text-center">
                  <TrendingUp className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">No progress updates yet</p>
                  <Button
                    onClick={() => setAddProgressOpen(true)}
                    className="mt-4 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                  >
                    Add First Update
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* SJA Tab */}
          <TabsContent value="sja" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                Safety Job Analysis (SJA)
              </h3>
              <Button
                onClick={() => navigate(`/local-projects/${project.project_id}/sja/new`)}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                New SJA
              </Button>
            </div>
            
            {/* SJA List */}
            <SJAList projectId={project.project_id} />
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-white">Documents & Files</h3>
              <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </div>
            
            {project.documents && project.documents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {project.documents.map((doc) => (
                  <Card key={doc.file_id} className="bg-white/5 border-white/10 hover:border-[#23F7DD]/30 transition-colors">
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{doc.file_name}</p>
                        <p className="text-xs text-[#64748B]">{doc.category} • {new Date(doc.created_at).toLocaleDateString()}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="text-[#94A3B8] hover:text-white">
                        <Download className="w-4 h-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">No documents uploaded yet</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Control Reports Tab */}
          <TabsContent value="control-reports" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-white">Scaffold Control Reports</h3>
              <Link to={`/local-projects/${projectId}/control-report/new`}>
                <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                  <Plus className="w-4 h-4 mr-2" />
                  New Report
                </Button>
              </Link>
            </div>
            
            {project.control_reports && project.control_reports.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {project.control_reports.map((report) => (
                  <Link key={report.report_id} to={`/local-projects/${projectId}/control-report/${report.report_id}`}>
                    <ControlReportCard report={report} />
                  </Link>
                ))}
              </div>
            ) : (
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardContent className="py-12 text-center">
                  <ClipboardCheck className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">No control reports created yet</p>
                  <Link to={`/local-projects/${projectId}/control-report/new`}>
                    <Button className="mt-4 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                      Create First Report
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Add Progress Dialog */}
        <AddProgressDialog
          open={addProgressOpen}
          onOpenChange={setAddProgressOpen}
          projectId={projectId}
          onAdded={fetchProject}
        />
      </div>
    </DashboardLayout>
  );
}
